import { formatShortDate } from "@/lib/conversation/time-format"

import type { SelfLearningMode } from "./self-learning"
import { MAX_CHANGE_LINE_LENGTH } from "./suggestions/schema"

/** Every customer-facing word about the Self-learning setting. Client-safe. */

/** The text a person's accept edits, against the agent's limit. */
export interface InstructionsRoom {
  length: number;
  limit: number;
}

export const SELF_LEARNING_LABEL = "Self-learning"
export const RECOMMENDED_BADGE = "Recommended"

export interface SelfLearningCopy {
  label: string;
  description: string;
  recommended?: boolean;
  warning?: { tone: "caution" | "danger"; text: string };
}

export const SELF_LEARNING_COPY: Record<SelfLearningMode, SelfLearningCopy> = {
  off: {
    label: "Off",
    description:
      "Learns nothing new and closes the suggestions waiting for you. Changes already made stay, and are still " +
      "taken back if answers get worse.",
  },
  suggest: {
    label: "Suggest only",
    description: "Prepares improvements. Each one waits for your OK.",
  },
  automatic: {
    label: "Automatic",
    recommended: true,
    description:
      "Improves its instructions and its knowledge base by itself. Asks you before changing text you wrote, rules " +
      "like discounts, refunds, handover and personal data, or retrying a change you said no to or took out.",
  },
  extended: {
    label: "Extended",
    description:
      "Also rewrites text you wrote, and always adds what it learns to your knowledge base. Asks you only about " +
      "rules like discounts, refunds, handover and personal data, and before retrying a change you said no to.",
    warning: { tone: "caution", text: "May reword instructions you wrote yourself." },
  },
  full: {
    label: "Full autopilot",
    description:
      "Decides everything by itself, including rules like discounts, refunds, handover and personal data. Never " +
      "asks, and never retries a change you said no to.",
    warning: {
      tone: "danger",
      text: "May change how the agent handles discounts, refunds and handing customers to your team.",
    },
  },
}

/** Automatic's option; the other writing modes always add articles, Suggest only never does. */
export const ARTICLES_OPTION_COPY = {
  label: "Add what it learns to the knowledge base by itself",
  off: "New facts wait for your OK",
} as const

export const IN_EVERY_MODE_NOTE =
  "In every mode: each change is checked and taken back automatically if answers get worse. Every change is listed " +
  "under Changes, with Undo, and a change to the instructions is in History too. A change you undo is never made " +
  "again. Articles wait when your knowledge base is full or their document is turned off, and an agent without its " +
  "own instructions asks before every change."

export const NO_OWN_INSTRUCTIONS_LINE = "Acts as Suggest only until this agent has its own instructions"

export function instructionsRoomLabel({ length, limit }: InstructionsRoom): string {
  return `${length.toLocaleString("en-US")} of ${limit.toLocaleString("en-US")} characters`
}

/** In whole lines a change may take, so «room» reads as how much more the agent can learn. */
export function instructionsRoomNote(room: InstructionsRoom): string {
  const lines = roomLines(room)
  if (lines === 0) return "Full: new lines can't be added until the instructions are shortened"
  return lines === 1 ? "Room for about 1 more line" : `Room for about ${lines.toLocaleString("en-US")} more lines`
}

export function instructionsFull(room: InstructionsRoom): boolean {
  return roomLines(room) === 0
}

export function roomLines({ length, limit }: InstructionsRoom): number {
  return Math.max(0, Math.floor((limit - length) / MAX_CHANGE_LINE_LENGTH))
}

export function learningSinceLine(since: string, timezone: string): string {
  return `Learning since ${formatShortDate(since, timezone)}`
}

/** Whom the Overview card speaks of: the selected agent, or a tenant's only one, else every agent. */
export type Learner = "agent" | "agents"

export function learningHeading(learner: Learner, learning: boolean): string {
  switch (learner) {
    case "agent":
      return learning ? "Your agent is learning" : "Your agent isn't learning"
    case "agents":
      return learning ? "Your agents are learning" : "Your agents aren't learning"
    default: {
      const exhaustive: never = learner
      return exhaustive
    }
  }
}

export function agentsLearningLine(learning: number, total: number): string {
  return `Self-learning on for ${learning.toLocaleString()} of ${total.toLocaleString()} agents`
}

export function noChangesLine(learner: Learner, learning: boolean): string {
  switch (learner) {
    case "agent":
      return learning
        ? "No changes yet — they appear here as the agent learns from conversations it didn't resolve."
        : "No changes yet — the agent learns nothing new while Self-learning is off."
    case "agents":
      return learning
        ? "No changes yet — they appear here as your agents learn from conversations they didn't resolve."
        : "No changes yet — your agents learn nothing new while Self-learning is off."
    default: {
      const exhaustive: never = learner
      return exhaustive
    }
  }
}

export const FULL_AUTOPILOT_CONFIRM = {
  body:
    "The agent will change its instructions without asking you — including how it handles discounts, refunds, " +
    "handing customers to your team and personal data.",
  protectionsHeading: "What still protects you",
  protections: [
    "Each change is checked and taken back automatically if answers get worse.",
    "Every change is listed under Changes, with Undo.",
    "A change you undo is never made again.",
  ],
  acknowledgement: "I understand the agent may change these rules by itself",
  confirm: "Turn on Full autopilot",
} as const

export function fullAutopilotTitle(agentName: string): string {
  return `Turn on Full autopilot for ${agentName}?`
}
